  <!--
 Copyright 2022 by domX Private Company

 All rights reserved,
 also regarding any disposal, exploitation, reproduction,
 editing, distribution, as well as in the event of
 applications for industrial property rights.

 This software contains proprietary and confidential
 information of domX Private Company.
 The disclosure of confidential information without
 express authorization is prohibited.

 This software may include Open Source Software.
 As far as Open Source Software is included,
 your rights for the respective Open Source Software
 components are governed by the applicable
 Open Source Software licenses.
-->

<template class="fill-height">
  <div class="whitesmoke mt-0 pt-0">
    <v-row no-gutters class="mx-5">
      <v-col cols="4" class="text-right pr-0">
          <v-menu
            ref="dateRangeMenuState"
            v-model="dateRangeMenuState"
            :close-on-content-click="false"
            
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                class="mt-0 pt-0"
                v-model="dateRangeText"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
              <v-date-picker
                v-model="dateRange"
                no-title
                range
                scrollable
                width="690px"
                @change="dateRangeUpdated"
              >
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="dateRangeMenuState = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.dateRangeMenuState.save(dateRange)"
                >
                  OK
                </v-btn>
              </v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="8" class="text-right ">
          <v-btn
            v-if="userHasExternalAPIAccess"
            class="ma-0"
            icon
            color="textColor2"
            @click="openExportModal"
          >
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
      </v-col>
    </v-row>

    <v-list class="whitesmoke tab" min-width="500px">
      <v-list-item>
        <v-list-item-content>
          <v-row>
            <DeviceSideviewLineChart 
              :cols="6" 
              chartName="power"
              :deviceId="this.device.info.device_id"
              :dataMetrics="['power_a','power_b']"
              :dateRange="dateForCharts"
              @update-date-range="updateDateRangeFromCharts"
              />
            <DeviceSideviewBarChart 
              :cols="6" 
              chartName="energy"
              :deviceId="this.device.info.device_id"
              typeOfData="metadata"
              :dataMetrics="['kwh_a','kwh_b']"
              :dateRange="dateForCharts"
              @update-date-range="updateDateRangeFromCharts"
            />
            <DeviceSideviewLineChart 
              :cols="6" 
              chartName="voltage"
              :deviceId="this.device.info.device_id"
              :dataMetrics="['voltage_a', 'voltage_b']"
              :dateRange="dateForCharts"
              @update-date-range="updateDateRangeFromCharts"
            />
            <DeviceSideviewLineChart 
              :cols="6" 
              chartName="relay"
              :deviceId="this.device.info.device_id"
              :dataMetrics="['relay_a']"
              :dateRange="dateForCharts"
              @update-date-range="updateDateRangeFromCharts"
            />
          </v-row>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { TimeUtil } from '@/helpers/TimeUtil'
import { commonModule } from '@/store/modules/store-accessor'
import ApexChart from "vue-apexcharts";
import DeviceSideviewLineChart from '@/components/Charts/DeviceSideviewLineChart.vue';
import DeviceSideviewBarChart from '@/components/Charts/DeviceSideviewBarChart.vue';
import config from "@/config";
import i18n from "@/i18n";
import dayjs from 'dayjs';
import _ from 'lodash';
import KeycloakSession from '@/helpers/keycloak-session'

@Component({
  components: {
    ApexChart,
    DeviceSideviewLineChart,
    DeviceSideviewBarChart
  }
})
export default class SinglePhaseEnergyMeterChartView extends Vue {
  @Prop() device: Record<string, any>;

  dateRangeMenuState = false;

  dateRange = [dayjs().startOf('week').format('YYYY-MM-DD'), dayjs().endOf('day').format('YYYY-MM-DD')];
  dateForCharts = { date_from: dayjs().startOf('week').valueOf() * 1000000, date_to: dayjs().endOf('week').valueOf() * 1000000 };

  get kcSession() {
    return new KeycloakSession()
  }

  get userHasExternalAPIAccess () {
    return this.kcSession.userHasExternalAPIAccess();
  }

  dateRangeUpdated(date: any) {
    let sorted = date.sort((a: any, b: any) => {
      var dateA = dayjs(a).unix(),
          dateB = dayjs(b).unix();
      if (dateB > dateA) {
        return -1;
      } else {
        return 1;
      }
    })
    let dateFrom = dayjs(sorted[0]).startOf('day').valueOf() * 1000000;
    let dateTo = dayjs(sorted[1]).endOf('day').valueOf() * 1000000;
    this.dateForCharts = { date_from: dateFrom, date_to: dateTo }
  }

  updateDateRangeFromCharts(dateObj: any) {
    this.dateRange = [dayjs.unix(dateObj.date_from / 1000000000).format('YYYY-MM-DD HH:mm'), dayjs.unix(dateObj.date_to / 1000000000).format('YYYY-MM-DD HH:mm')];
    this.dateForCharts = _.cloneDeep(dateObj);
  }

  get dateRangeText () {
    // let dateRangeText = this.dateRange.map((item) => dayjs(item).format('YYYY-MM-DD HH:mm'))
    let sorted: any = this.dateRange.sort((a: any, b: any) => {
      var dateA = dayjs(a).unix(),
          dateB = dayjs(b).unix();
      if (dateB > dateA) {
        return -1;
      } else {
        return 1;
      }
    })

    // The V-datepicker, returns only date strings, and we forcefully add startof day end of day to fetch the data for 24-hour days. 
    // This condition checks if the string already contains hours:minutes string. If it does then we do not format it to 
    // to add the HH:mm. 
    if(/:/.test(this.dateRange[0])) {
      return sorted.join(' - ')
    }
    else {
      let formattedDates = [] 
      
      formattedDates.push(dayjs(sorted[0]).startOf('day').format('YYYY-MM-DD HH:mm'))

      if(sorted[1]) {
        formattedDates.push(dayjs(sorted[1]).endOf('day').format('YYYY-MM-DD HH:mm'))
      }

      return formattedDates.join(' - ')
    }
  }

  openExportModal() {
    commonModule.showModal({ name: 'export-device-data-modal', payload: { device: this.device}})
  }
};
</script>

<style scoped lang="scss">
.tab {
  max-height: calc(80vh - 230px) !important;
  overflow-y: auto;
}

.expansion-item {
  display: block;
  width: 50px;
  word-wrap: break-word;
}

.is_disabled {
  color: #b9b9b9!important;
  background-color:#e6e6e6;

}
</style>
